import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useResolveFormSchema } from "../../../../../_hooks/useResolveFormSchema";
import fieldsToSections from "../../../../../_utils/fieldsToSections";
import { processMarketFormSchema } from "../utils/marketFormRules";

/**
 * Builds MarketFormModal's field-schema sections from the org's active
 * "market" object customizations, resolving any dynamic (external
 * reference) fields' data along the way - mirrors
 * _features/company/modals/CompanyFormModal/hooks/useCompanyFormSchema.js
 * exactly (Markets is the dynamic-form engine's second consumer).
 *
 * `processMarketFormSchema` (forcing country to multiSelect) runs here,
 * BEFORE useResolveFormSchema - not after, on the already-resolved schema,
 * the way MarketFormModal used to call it. useResolveFormSchema reads
 * `dynamicConfig.referenceType`/`type` to decide which items to fetch and
 * build; applying overrides afterward just relabels an already-resolved
 * field without re-resolving its `items`.
 *
 * `rowData` (edit mode only) is threaded through to useResolveFormSchema
 * as `filters.currentMarketId` - the "marketCountries" referenceType needs
 * to know which market is being edited so it doesn't flag that market's
 * own already-selected countries as taken by someone else.
 */
export function useMarketFormSchema(rowData) {
  const objectCustomizations = useSelector(
    (state) => state?.objectCustomizationFeature?.objectCustomizations
  );

  const marketCustomization = useMemo(() => {
    const customization = objectCustomizations?.data?.market;
    return Array.isArray(customization) ? customization : [];
  }, [objectCustomizations]);

  const marketFormSchema = useMemo(() => {
    const fields = marketCustomization.filter(
      (oc) => oc.status === "active" && oc.showDuringCreation
    );
    return processMarketFormSchema(fieldsToSections(fields));
  }, [marketCustomization]);

  const resolvedSchema = useResolveFormSchema(marketFormSchema, {
    currentMarketId: rowData?._id,
  });

  return useMemo(() => resolvedSchema, [resolvedSchema]);
}
